import capitalize from 'lodash/capitalize';
import kebabCase from 'lodash/kebabCase';
import some from 'lodash/some';
import { OR, AND, NOT } from 'utils/combinatorTypes';
import { getTextWidth, tooltipFontConfig } from 'ui/workflows/flowchart';
import { getComparatorDescriptor, parseQueryValue } from '../queryStringUtils';
import { WORKFLOW_TRIGGERS, TRIGGER_METADATA } from '../../constants';
// import { StartNodeData, SingleValueSearchField, Workflow } from '../../types';

const isResourceValueValid = (value) => value != null && value !== '';

const getResourceField = ({ nodeClass } = {}) => TRIGGER_METADATA[nodeClass]?.resourceFieldName;

const getResourceValue = (nodeData = {}) => {
  const fieldName = getResourceField(nodeData);
  const fieldValue = nodeData[fieldName];

  return fieldValue == null ? undefined : `${fieldValue}`;
};

const isDataForNodeClassValid = (nodeData /*: StartNodeData */) /*: boolean */ => {
  const { nodeClass } = nodeData;
  const resourceField = getResourceField(nodeData);

  // if there is a resource field, make sure it is set
  if (resourceField && !isResourceValueValid(nodeData[resourceField])) return false;

  if (nodeClass === WORKFLOW_TRIGGERS.SCHEDULED_RECURRING_LIST) {
    const { emailListId, recurDays, startDate, endDate, timeZone } = nodeData;
    // Make sure that all of these are either a number or a string

    const areNumberOrString = [emailListId, recurDays, startDate, timeZone].every(
      (val) => Number.isFinite(val) || typeof val === 'string',
    );
    const startDateIsBeforeEndDate = endDate ? new Date(startDate) < new Date(endDate) : true;
    return areNumberOrString && startDateIsBeforeEndDate;
  }

  return true;
};

export const isValidEntraceLimits = (workflow /*: Workflow */) => {
  const isNullOrUndefined = (val) => typeof val === 'undefined' || val === null;
  const { workflowLifetimeLimit, workflowSimultaneousLimit } = workflow;
  if (isNullOrUndefined(workflowLifetimeLimit) && isNullOrUndefined(workflowSimultaneousLimit))
    return true;
  if (
    isNullOrUndefined(workflowLifetimeLimit) &&
    workflowSimultaneousLimit > 0 &&
    workflowSimultaneousLimit <= 100
  )
    return true;
  if (
    isNullOrUndefined(workflowSimultaneousLimit) &&
    workflowLifetimeLimit > 0 &&
    workflowLifetimeLimit <= 100
  )
    return true;
  return (
    workflowLifetimeLimit > 0 &&
    workflowLifetimeLimit <= 100 &&
    workflowSimultaneousLimit > 0 &&
    workflowSimultaneousLimit <= 100
  );
};

const getDescTop = (nodeData /*: StartNodeData */) /*: string */ => {
  const { nodeClass } = nodeData;
  const triggerNotFound =
    typeof nodeClass === 'string'
      ? capitalize(kebabCase(nodeClass.replace('Trigger', '')).split('-').join(' '))
      : 'No Trigger Defined';

  return TRIGGER_METADATA[nodeClass]?.label || triggerNotFound;
};

const isQueryValid = (query /*: SingleValueSearchField */ = {}) /*: boolean */ => {
  const { dataType, field, comparatorType, value } = query;
  return !some(
    [dataType, field, comparatorType, value],
    (prop) => prop === null || typeof prop === 'undefined',
  );
};

const hasInvalidFilters = (nodeData /*: StartNodeData */) /*: boolean */ =>
  some(nodeData?.searchCombo?.searchQueries || [], (q) => !isQueryValid(q));

const hasValidFilters = (nodeData /*: StartNodeData */) /*: boolean */ =>
  some(nodeData?.searchCombo?.searchQueries || [], isQueryValid);

const hasFilters = (nodeData /*: StartNodeData */) /*: boolean */ =>
  (nodeData?.searchCombo?.searchQueries || []).length > 0;

const getHasFilterText = (nodeData /*: StartNodeData */) /*: boolean */ =>
  hasFilters(nodeData) && hasValidFilters(nodeData);

const getDescBottom = (nodeData /*: StartNodeData */) /*: string */ =>
  getHasFilterText(nodeData) ? 'Filtered' : '';

// type QueryTypes = 'where' | 'and' | 'or' | 'not' | 'nor';
// interface QueryTypeSpacingMap {
//   [key: QueryTypes] /*: string */;
// }

/**
 * Because we can't align right in a span (as we usually would),
 * this is how we figure out spacing for the query words with
 * the tooltips
 */
const getSpacingForQueryTypes = () /*: QueryTypeSpacingMap */ => {
  const [spaceWidth, whereWidth, andWidth, orWidth] = [
    ' ',
    'where',
    AND.toLowerCase(),
    OR.toLowerCase(),
  ].map((val) => getTextWidth(val, tooltipFontConfig));

  const getSpaces = (comp) =>
    Array.from({ length: Math.round((whereWidth - comp) / spaceWidth) }, () => ' ').join('');

  return {
    where: '',
    and: getSpaces(andWidth),
    or: getSpaces(orWidth),
    not: '',
    nor: '',
  };
};

const querySpacing /*: QueryTypeSpacingMap */ = getSpacingForQueryTypes();

const getTooltipText = ({ searchCombo } /*: StartNodeData */) /*: string */ => {
  if (!searchCombo) return '';

  const { combinator, searchQueries } = searchCombo;
  const isNot = combinator === NOT;

  return searchQueries
    .filter(isQueryValid) // filter out invalid queries
    .map((query, i) => {
      const { field, comparatorType, value, fieldType } = query;
      let queryType;
      if (i === 0) {
        queryType = isNot ? 'not' : 'where';
      } else {
        queryType = isNot ? 'nor' : combinator.toLowerCase();
      }
      return `${querySpacing[queryType]}${queryType} ${field} ${getComparatorDescriptor(
        comparatorType,
      )} ${parseQueryValue(value, fieldType, comparatorType)}`;
    })
    .join('\n');
};

const getTextSegments = (nodeData /*: StartNodeData */ = {}) /*: TextSegments */ => {
  const resourceName = nodeData?.resource?.name || getResourceValue(nodeData);
  const segments = [[{ text: getDescTop(nodeData) }]];
  const hasFilterText = getHasFilterText(nodeData);

  if (resourceName) {
    segments.push([{ text: resourceName }]);
  }

  if (hasFilterText) {
    const descBottomText = getDescBottom(nodeData);

    segments.push([
      {
        text: descBottomText,
        tooltipText: getTooltipText(nodeData),
      },
    ]);
  }

  return { nodeDescBottom: { segments, showTooltipIfTruncated: true } };
};

export const startUtils = {
  getTextSegments,
  getResourceField,
  getResourceValue,
  isResourceValueValid,
  // NOTE: put other validators in here as they're coded
  isValid: (nodeData /*: StartNodeData */, workflowData /*: Workflow */) /*: boolean */ =>
    !hasInvalidFilters(nodeData) &&
    isDataForNodeClassValid(nodeData) &&
    isValidEntraceLimits(workflowData),
};

export default startUtils;
